import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatEuro, API_BASE } from "@/lib/api";
import AppHeader from "@/components/AppHeader";
import { Users, Banknote, Receipt, Wallet, TrendingUp, Calendar, FileSpreadsheet, Lock, Unlock, RotateCcw } from "lucide-react";

export default function SeasonPage() {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (from) params.from_date = from;
      if (to) params.to_date = to;
      const { data } = await api.get("/season/summary", { params });
      setData(data);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const apply = (e) => {
    e.preventDefault();
    load();
  };

  const exportExcel = () => {
    const params = new URLSearchParams();
    if (from) params.set("from_date", from);
    if (to) params.set("to_date", to);
    const qs = params.toString();
    window.open(`${API_BASE}/season/export.xlsx${qs ? "?" + qs : ""}`, "_blank");
  };

  const closeSeason = async () => {
    if (!window.confirm("Alle Vorstellungen im gewählten Zeitraum als abgeschlossen markieren?")) return;
    const params = {};
    if (from) params.from_date = from;
    if (to) params.to_date = to;
    try {
      const { data } = await api.post("/season/close", null, { params });
      window.alert(`${data.closed} Vorstellung(en) abgeschlossen.`);
      load();
    } catch (e) {
      window.alert("Fehler: " + (e.response?.data?.detail || e.message));
    }
  };

  const reopenSeason = async () => {
    if (!window.confirm("Alle Vorstellungen im Zeitraum wieder öffnen?")) return;
    const params = {};
    if (from) params.from_date = from;
    if (to) params.to_date = to;
    try {
      const { data } = await api.post("/season/reopen", null, { params });
      window.alert(`${data.reopened} Vorstellung(en) wieder geöffnet.`);
      load();
    } catch (e) {
      window.alert("Fehler: " + (e.response?.data?.detail || e.message));
    }
  };

  const resetSeason = async () => {
    const msg = (from || to)
      ? `ALLE Reservierungen und Transaktionen der Vorstellungen im gewählten Zeitraum löschen?\n\nDie Vorstellungen selbst bleiben erhalten. Auch die Rechnungsnummern für ${new Date().getFullYear()} werden zurückgesetzt.\n\nDas kann NICHT rückgängig gemacht werden.`
      : `ACHTUNG: Du hast keinen Zeitraum gewählt — das löscht ALLE Reservierungen und Transaktionen ALLER Vorstellungen!\n\nWeiter?`;
    if (!window.confirm(msg)) return;
    if (!window.confirm("Wirklich endgültig zurücksetzen?")) return;
    const params = {};
    if (from) params.from_date = from;
    if (to) params.to_date = to;
    try {
      const { data } = await api.post("/season/reset", null, { params });
      window.alert(`Zurückgesetzt: ${data.shows_affected} Vorstellung(en), ${data.reservations_deleted} Reservierungen, ${data.transactions_deleted} Transaktionen gelöscht.`);
      load();
    } catch (e) {
      window.alert("Fehler: " + (e.response?.data?.detail || e.message));
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      <AppHeader
        title="Saison-Statistik"
        right={
          <>
            <button data-testid="season-reset-btn" onClick={resetSeason}
              className="h-11 px-4 rounded-md border border-amber-200 text-amber-800 hover:bg-amber-50 font-bold flex items-center gap-2 text-sm"
              title="Reservierungen + Transaktionen löschen (Vorstellungen behalten)">
              <RotateCcw className="h-4 w-4" /> Saison zurücksetzen
            </button>
            <button data-testid="season-reopen-btn" onClick={reopenSeason}
              className="h-11 px-4 rounded-md border border-zinc-200 hover:bg-zinc-50 font-semibold flex items-center gap-2 text-sm">
              <Unlock className="h-4 w-4" /> Wieder öffnen
            </button>
            <button data-testid="season-close-btn" onClick={closeSeason}
              className="h-11 px-4 rounded-md border border-zinc-200 hover:bg-zinc-50 font-bold flex items-center gap-2 text-sm">
              <Lock className="h-4 w-4" /> Abschließen
            </button>
            <button data-testid="season-export-btn" onClick={exportExcel}
              className="h-11 px-4 rounded-md bg-zinc-950 hover:bg-zinc-800 text-white font-bold flex items-center gap-2 text-sm">
              <FileSpreadsheet className="h-4 w-4" /> Excel
            </button>
          </>
        }
      />
      <main className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        <form onSubmit={apply} className="bg-white border border-zinc-200 rounded-xl p-5 flex flex-wrap items-end gap-4">
          <div>
            <label className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-500 mb-2 block">Von</label>
            <input data-testid="season-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)}
              className="h-11 px-3 border border-zinc-300 rounded-md focus:outline-none focus:ring-2 focus:ring-zinc-950" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-500 mb-2 block">Bis</label>
            <input data-testid="season-to" type="date" value={to} onChange={(e) => setTo(e.target.value)}
              className="h-11 px-3 border border-zinc-300 rounded-md focus:outline-none focus:ring-2 focus:ring-zinc-950" />
          </div>
          <button data-testid="season-apply" type="submit"
            className="h-11 px-5 rounded-md bg-zinc-950 hover:bg-zinc-800 text-white font-bold">Anwenden</button>
          {(from || to) && (
            <button type="button" onClick={() => { setFrom(""); setTo(""); setTimeout(load, 0); }}
              className="h-11 px-4 rounded-md border border-zinc-200 hover:bg-zinc-50 font-semibold text-sm">
              Filter zurücksetzen
            </button>
          )}
          <p className="ml-auto text-sm text-zinc-500">
            {data ? `${data.show_count} Vorstellung${data.show_count !== 1 ? "en" : ""}` : ""}
          </p>
        </form>

        {loading || !data ? (
          <p className="text-zinc-500">Lade…</p>
        ) : data.show_count === 0 ? (
          <div className="bg-white border border-dashed border-zinc-300 rounded-xl p-16 text-center">
            <p className="text-xl font-bold mb-2">Keine Vorstellungen im Zeitraum</p>
            <p className="text-zinc-500">Wähle einen anderen Zeitraum oder lege eine neue Vorstellung an.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <Metric label="Vorstellungen" value={data.show_count} icon={Calendar} testid="season-metric-shows" raw />
              <Metric label="Besucher gesamt" value={data.total_visitors} icon={Users} testid="season-metric-visitors" raw />
              <Metric label="Bareinnahmen" value={data.cash_total} icon={Banknote} accent="emerald" testid="season-metric-cash" />
              <Metric label="Überweisungen" value={data.invoice_total} icon={Receipt} accent="blue" testid="season-metric-invoice" />
              <Metric label="Gesamt" value={data.grand_total} icon={TrendingUp} accent="dark" testid="season-metric-total" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
                <div className="px-6 py-4 border-b border-zinc-100">
                  <h3 className="text-xl font-black tracking-tight">Karten nach Kategorie</h3>
                </div>
                <table className="w-full">
                  <thead className="bg-zinc-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs uppercase tracking-widest text-zinc-500">Kategorie</th>
                      <th className="px-6 py-3 text-right text-xs uppercase tracking-widest text-zinc-500">Anzahl</th>
                      <th className="px-6 py-3 text-right text-xs uppercase tracking-widest text-zinc-500">Summe</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.by_category.map((c) => (
                      <tr key={c.key} data-testid={`season-cat-${c.key}`} className="border-b border-zinc-100">
                        <td className="px-6 py-3 font-semibold text-sm">{c.label}</td>
                        <td className="px-6 py-3 text-right font-mono tabular-nums">{c.qty}</td>
                        <td className="px-6 py-3 text-right font-mono font-bold tabular-nums">{formatEuro(c.revenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="bg-white border border-zinc-200 rounded-xl p-6 flex flex-col justify-between">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-500">Wechselgeld in der Kassa</p>
                  <p className="font-mono text-3xl font-black tabular-nums mt-1" data-testid="season-startcash">{formatEuro(data.total_start_cash)}</p>
                  <p className="text-xs text-zinc-500 mt-1">Bleibt konstant in der Kassa — wird nicht pro Vorstellung addiert</p>
                </div>
                <div className="mt-4">
                  <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-500">Ø Einnahme pro Vorstellung</p>
                  <p className="font-mono text-3xl font-black tabular-nums mt-1">
                    {formatEuro(data.show_count > 0 ? data.grand_total / data.show_count : 0)}
                  </p>
                </div>
                <div className="mt-4">
                  <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-500">Ø Besucher pro Vorstellung</p>
                  <p className="font-mono text-3xl font-black tabular-nums mt-1">
                    {data.show_count > 0 ? Math.round(data.total_visitors / data.show_count) : 0}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
              <div className="px-6 py-4 border-b border-zinc-100">
                <h3 className="text-xl font-black tracking-tight">Vorstellungen im Zeitraum</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-zinc-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs uppercase tracking-widest text-zinc-500">Datum</th>
                      <th className="px-6 py-3 text-left text-xs uppercase tracking-widest text-zinc-500">Titel</th>
                      <th className="px-6 py-3 text-right text-xs uppercase tracking-widest text-zinc-500">Besucher</th>
                      <th className="px-6 py-3 text-right text-xs uppercase tracking-widest text-zinc-500">Bar</th>
                      <th className="px-6 py-3 text-right text-xs uppercase tracking-widest text-zinc-500">Überweisung</th>
                      <th className="px-6 py-3 text-right text-xs uppercase tracking-widest text-zinc-500">Gesamt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.shows.map((s) => (
                      <tr
                        key={s.id}
                        data-testid={`season-show-${s.id}`}
                        onClick={() => navigate(`/shows/${s.id}/abschluss`)}
                        className="border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer"
                      >
                        <td className="px-6 py-3 text-sm font-mono">{s.date} {s.time}</td>
                        <td className="px-6 py-3 font-semibold">{s.title}{s.closed && <span className="ml-2 text-[10px] uppercase tracking-widest font-bold text-zinc-500">abgeschlossen</span>}</td>
                        <td className="px-6 py-3 text-right font-mono tabular-nums">{s.visitors}</td>
                        <td className="px-6 py-3 text-right font-mono tabular-nums">{formatEuro(s.cash_total)}</td>
                        <td className="px-6 py-3 text-right font-mono tabular-nums">{formatEuro(s.invoice_total)}</td>
                        <td className="px-6 py-3 text-right font-mono font-bold tabular-nums">{formatEuro(s.grand_total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

function Metric({ label, value, icon: Icon, accent = "default", testid, raw = false }) {
  const styles = {
    default: "bg-white border-zinc-200",
    emerald: "bg-emerald-50 border-emerald-200",
    blue: "bg-blue-50 border-blue-200",
    dark: "bg-zinc-950 border-zinc-950 text-white",
  };
  const labelStyle = accent === "dark" ? "text-zinc-300" : "text-zinc-500";
  return (
    <div className={`rounded-xl border p-5 ${styles[accent]}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`h-4 w-4 ${accent === "dark" ? "text-zinc-300" : "text-zinc-500"}`} />
        <p className={`text-[10px] uppercase tracking-[0.25em] font-bold ${labelStyle}`}>{label}</p>
      </div>
      <p data-testid={testid} className="font-mono text-3xl font-black tabular-nums tracking-tighter">
        {raw ? value : formatEuro(value)}
      </p>
    </div>
  );
}
